import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { ArrowsClockwise } from "@phosphor-icons/react";
import HealthSummaryCards from "@/components/delivery-health/HealthSummaryCards";
import DeliveryHealthTable from "@/components/delivery-health/DeliveryHealthTable";
import DeliveryDrillModal from "@/components/delivery-health/DeliveryDrillModal";
import AlertRulesSection from "@/components/delivery-health/AlertRulesSection";
import { relativeTime } from "@/components/delivery-health/deliveryHealthHelpers";
import { DeliveryAlertsCard } from "@/components/delivery/DeliveryAlertsCard";

/**
 * Delivery health page. Fetches destinations + alerts and manages the drill
 * modal. Rendering is delegated to the sub-components under
 * `components/delivery-health/`.
 */
export default function DeliveryHealth() {
  const [rows, setRows] = useState(null);
  const [generatedAt, setGeneratedAt] = useState(null);
  const [drillId, setDrillId] = useState(null);
  const [drill, setDrill] = useState(null);
  const [busy, setBusy] = useState(false);
  const [selected, setSelected] = useState({});
  const [alerts, setAlerts] = useState([]);
  const [showAlertForm, setShowAlertForm] = useState(false);
  // Iter-63.1 — server-side pagination + search + fleet summary.
  const [page, setPage] = useState(1);
  const [pageSize] = useState(50);
  const [qInput, setQInput] = useState("");
  const [q, setQ] = useState("");
  const [pagination, setPagination] = useState({ total: 0, page: 1, page_size: 50, has_next: false });
  const [fleetSummary, setFleetSummary] = useState(null);

  const load = async (opts = {}) => {
    try {
      const pg = opts.page ?? page;
      const params = new URLSearchParams();
      params.set("page", String(pg));
      params.set("page_size", String(pageSize));
      if (q) params.set("q", q);
      const [{ data }, { data: a }] = await Promise.all([
        api.get(`/delivery/health?${params.toString()}`),
        api.get("/delivery/health-alerts").catch(() => ({ data: { alerts: [] } })),
      ]);
      setRows(data.destinations || []);
      setGeneratedAt(data.generated_at);
      setPagination(data.pagination || { total: 0, page: 1, page_size: 50, has_next: false });
      setFleetSummary(data.fleet_summary || null);
      setAlerts(a.alerts || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load");
    }
  };

  useEffect(() => { load({ page }); /* eslint-disable-next-line */ }, [page, q]);

  // Debounce search input to avoid a fetch per keystroke.
  useEffect(() => {
    const t = setTimeout(() => { setPage(1); setQ(qInput.trim()); }, 300);
    return () => clearTimeout(t);
  }, [qInput]);

  // Load queue drill-down whenever `drillId` changes.
  useEffect(() => {
    if (!drillId) { setDrill(null); setSelected({}); return; }
    (async () => {
      setBusy(true);
      try {
        const { data } = await api.get(`/delivery/destinations/${drillId}/queue`);
        setDrill(data);
        setSelected({});
      } catch (e) {
        toast.error(e.response?.data?.detail || "Failed");
        setDrillId(null);
      } finally {
        setBusy(false);
      }
    })();
  }, [drillId]);

  const bulkAction = async (op) => {
    if (!drillId) return;
    const selectedIds = Object.keys(selected).filter((k) => selected[k]);
    const attemptAll = !selectedIds.length;
    if (attemptAll && !confirm(`No rows selected — apply "${op}" to ALL pending retries for this destination?`)) {
      return;
    }
    const path = op === "retry-now" ? "retry-now" : "cancel";
    try {
      const body = attemptAll ? { all: true } : { attempt_ids: selectedIds };
      if (op === "cancel") body.reason = "manual cancel from health board";
      const { data } = await api.post(`/delivery/destinations/${drillId}/queue/${path}`, body);
      toast.success(`${op}: ${data.modified} attempts updated`);
      const { data: q } = await api.get(`/delivery/destinations/${drillId}/queue`);
      setDrill(q);
      setSelected({});
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const deleteAlert = async (id) => {
    if (!confirm("Delete this alert rule?")) return;
    await api.delete(`/delivery/health-alerts/${id}`);
    toast.success("Deleted");
    load();
  };

  const evalAlert = async (id) => {
    try {
      const { data } = await api.post(`/delivery/health-alerts/${id}/evaluate`);
      if (data.breaches?.length) {
        toast.warning(`Fired: ${data.breaches.length} breach(es)`);
      } else if (data.skipped === "cooldown") {
        toast.info("Within cooldown — not firing");
      } else {
        toast.success("Evaluated — no breach");
      }
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  return (
    <div>
      <div className="flex items-end justify-between mb-6">
        <div>
          <div className="label-mono">Ops</div>
          <h1 className="font-chivo font-black text-4xl md:text-5xl uppercase tracking-tighter leading-none">
            Delivery health
          </h1>
          <div className="mt-1 text-sm text-zinc-600 max-w-2xl">
            Per-destination success rate over the last 24 hours and 7 days, plus pending retries in the queue.
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-[10px] uppercase tracking-widest text-zinc-500">
            Updated {relativeTime(generatedAt)}
          </span>
          <button onClick={load} data-testid="health-refresh"
            className="border border-black px-3 py-2 text-xs uppercase tracking-widest inline-flex items-center gap-2 hover:bg-black hover:text-white">
            <ArrowsClockwise size={12} weight="bold" /> Refresh
          </button>
        </div>
      </div>

      {rows === null && <div className="brut-card p-8 text-center label-mono">Loading…</div>}
      <DeliveryAlertsCard showClear />
      {rows && <HealthSummaryCards rows={rows} fleet={fleetSummary} />}

      {rows && (
        <div className="brut-card p-3 mb-3 flex flex-col md:flex-row md:items-center gap-2"
             data-testid="delivery-health-toolbar">
          <div className="relative flex-1 min-w-[200px]">
            <input type="text" value={qInput} onChange={(e) => setQInput(e.target.value)}
              placeholder="Search destination name…"
              className="w-full border border-black px-2 py-1.5 text-sm font-mono"
              data-testid="delivery-health-search" />
          </div>
          <div className="ml-auto flex items-center gap-2" data-testid="delivery-health-pagination">
            <span className="text-[10px] uppercase tracking-widest text-zinc-600">
              {pagination.total === 0
                ? "No destinations"
                : `Page ${pagination.page} of ${Math.max(1, Math.ceil((pagination.total || 0) / (pagination.page_size || 50)))} · ${pagination.total} total`}
            </span>
            <button onClick={() => setPage(Math.max(1, page - 1))} disabled={page <= 1}
              data-testid="delivery-health-prev"
              className="border border-black px-2 py-1 text-[11px] uppercase tracking-widest disabled:opacity-30 hover:bg-black hover:text-white">
              Prev
            </button>
            <button onClick={() => setPage(page + 1)} disabled={!pagination.has_next}
              data-testid="delivery-health-next"
              className="border border-black px-2 py-1 text-[11px] uppercase tracking-widest disabled:opacity-30 hover:bg-black hover:text-white">
              Next
            </button>
          </div>
        </div>
      )}
      {rows && !rows.length && (
        <div className="brut-card p-8 text-center label-mono" data-testid="health-empty">
          {q ? `No destinations match "${q}".` : "No delivery destinations configured yet."}
        </div>
      )}
      {rows && !!rows.length && <DeliveryHealthTable rows={rows} onDrill={setDrillId} />}

      <AlertRulesSection
        alerts={alerts}
        rows={rows}
        showForm={showAlertForm}
        setShowForm={setShowAlertForm}
        onReload={load}
        onDelete={deleteAlert}
        onEvaluate={evalAlert}
      />

      {drillId && (
        <DeliveryDrillModal
          drill={drill}
          busy={busy}
          selected={selected}
          setSelected={setSelected}
          onClose={() => setDrillId(null)}
          onBulkAction={bulkAction}
        />
      )}
    </div>
  );
}
